import { msg } from "@lingui/core/macro";
import { createSignal, For, onCleanup, onMount } from "solid-js";
import { createRoute } from "@tanstack/solid-router";
import { Keyboard } from "@fleet/icons";
import { PageHeader } from "@fleet/ui/page-header";
import { PageContainer } from "@fleet/ui/page-container";
import { appRoute } from "./route";
import { useI18n } from "@/providers/i18n-provider";
import { Card, CardContent } from "@fleet/ui/card";
import { VirtualKey } from "./profile/virtual-key";
import { ShortcutRow } from "@fleet/shortcuts";

function ShortcutsPage() {
  const { t } = useI18n();

  const isMac =
    typeof navigator !== "undefined" && /Mac|iPod|iPhone|iPad/.test(navigator.userAgent);
  const cmdKey = isMac ? "⌘" : "Ctrl";

  const [pressedKeys, setPressedKeys] = createSignal<Set<string>>(new Set());

  // Physical key event listeners
  onMount(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const key = e.key.toUpperCase();
      const code = e.code.toUpperCase();
      setPressedKeys((prev) => {
        const next = new Set([...prev, key, code]);
        // Special mappings for modifiers/special keys
        if (e.altKey) {
          next.add("ALT");
        }
        if (e.ctrlKey) {
          next.add("CTRL");
        }
        if (e.metaKey) {
          next.add("META");
        }
        if (e.shiftKey) {
          next.add("SHIFT");
        }
        if (e.key === " ") {
          next.add("SPACE");
        }
        return next;
      });
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      const key = e.key.toUpperCase();
      const code = e.code.toUpperCase();
      setPressedKeys((prev) => {
        const next = new Set(prev);
        next.delete(key);
        next.delete(code);
        if (!e.altKey) {
          next.delete("ALT");
        }
        if (!e.ctrlKey) {
          next.delete("CTRL");
        }
        if (!e.metaKey) {
          next.delete("META");
        }
        if (!e.shiftKey) {
          next.delete("SHIFT");
        }
        if (e.key === " ") {
          next.delete("SPACE");
        }
        return next;
      });
    };

    globalThis.addEventListener("keydown", handleKeyDown);
    globalThis.addEventListener("keyup", handleKeyUp);

    onCleanup(() => {
      globalThis.removeEventListener("keydown", handleKeyDown);
      globalThis.removeEventListener("keyup", handleKeyUp);
    });
  });

  const categories = () => [
    {
      title: t(msg`Global Actions`),
      shortcuts: [
        { keys: [cmdKey, "K"], desc: t(msg`Search tasks or lists`) },
        { keys: ["N"], desc: t(msg`Focus new task input`) },
        { keys: ["Alt", "N"], desc: t(msg`Create new custom list`) },
        { keys: ["?"], desc: t(msg`Open keyboard shortcuts help`) },
        { keys: ["Esc"], desc: t(msg`Close active panels or dialogs`) },
      ],
    },
    {
      title: t(msg`List Navigation`),
      shortcuts: [
        { keys: ["↑", "↓"], desc: t(msg`Move focus between tasks`) },
        { keys: ["K", "J"], desc: t(msg`Vim: Move focus up or down`) },
        { keys: ["Enter"], desc: t(msg`Open selected task details`) },
        { keys: ["Esc"], desc: t(msg`Clear task selection / focus`) },
      ],
    },
    {
      title: t(msg`Task Management (Focused)`),
      shortcuts: [
        { keys: ["Space"], desc: t(msg`Complete / Uncomplete task`) },
        { keys: ["E"], desc: t(msg`Alternative complete shortcut`) },
        { keys: ["I"], desc: t(msg`Star task / Mark as important`) },
        { keys: ["S"], desc: t(msg`Alternative star shortcut`) },
        { keys: ["Delete"], desc: t(msg`Delete selected task`) },
        { keys: ["Backspace"], desc: t(msg`Alternative delete shortcut`) },
      ],
    },
    {
      title: t(msg`Quick Views (Go To)`),
      shortcuts: [
        { keys: ["G", "I"], desc: t(msg`Go to Inbox`) },
        { keys: ["G", "T"], desc: t(msg`Go to My Day`) },
        { keys: ["G", "P"], desc: t(msg`Go to Planned`) },
        { keys: ["G", "M"], desc: t(msg`Go to Important`) },
        { keys: ["G", "A"], desc: t(msg`Go to All Tasks`) },
        { keys: ["G", "H"], desc: t(msg`Go to Dashboard`) },
      ],
    },
  ];

  // Visual Virtual Keyboard layout (active bindings only)
  const virtualKeys = [
    { label: "Esc", matchKeys: ["ESCAPE", "ESC"] },
    { label: "G", matchKeys: ["G"] },
    { label: "I", matchKeys: ["I"] },
    { label: "T", matchKeys: ["T"] },
    { label: "D", matchKeys: ["D"] },
    { label: "P", matchKeys: ["P"] },
    { label: "M", matchKeys: ["M"] },
    { label: "A", matchKeys: ["A"] },
    { label: "H", matchKeys: ["H"] },
    { label: "K", matchKeys: ["K"] },
    { label: "J", matchKeys: ["J"] },
    { label: "N", matchKeys: ["N"] },
    { label: "Alt", matchKeys: ["ALT"] },
    { label: "Space", matchKeys: ["SPACE", " "], class: "flex-grow md:max-w-[200px]" },
    { label: "E", matchKeys: ["E"] },
    { label: "S", matchKeys: ["S"] },
    { label: "Enter", matchKeys: ["ENTER"], class: "flex-grow md:max-w-[120px]" },
    { label: "Delete", matchKeys: ["DELETE"] },
    { label: "Backspace", matchKeys: ["BACKSPACE"] },
    { label: "↑", matchKeys: ["ARROWUP", "UP"] },
    { label: "↓", matchKeys: ["ARROWDOWN", "DOWN"] },
  ];

  return (
    <PageContainer maxWidth="4xl" class="space-y-8" containerClass="scrollbar-thin">
      {/* Header */}
      <PageHeader
        title={t(msg`Keyboard Shortcuts`)}
        description={t(
          msg`Master your workflow. Type any key below to see it light up in real-time!`,
        )}
        backTo="/profile"
        icon={Keyboard}
      />

      {/* Live Interactive Virtual Keyboard Panel */}
      <div class="p-6 md:p-8 bg-primary/5 border border-primary/10 rounded-2xl backdrop-blur-md space-y-6 shadow-xl">
        <h2 class="text-sm font-bold uppercase tracking-wider text-primary/80">
          {t(msg`Interactive Keyboard Visualizer`)}
        </h2>
        <div class="flex flex-wrap gap-2.5 justify-center md:justify-start">
          <For each={virtualKeys}>
            {(key) => (
              <VirtualKey
                label={key.label}
                isPressed={key.matchKeys.some((k) => pressedKeys().has(k.toUpperCase()))}
                class={key.class}
              />
            )}
          </For>
        </div>
        <div class="text-[10px] text-muted-foreground/80 italic">
          {t(msg`Press keys on your keyboard and watch them illuminate above!`)}
        </div>
      </div>

      {/* Full Categorized Cheat Sheet */}
      <div class="grid gap-6 md:grid-cols-2">
        <For each={categories()}>
          {(category) => (
            <Card class="border-border/10 bg-card/50 backdrop-blur-sm overflow-hidden">
              <CardContent class="p-5 space-y-4">
                <h3 class="text-xs font-bold uppercase tracking-wider text-primary px-1 border-b border-border/10 pb-2">
                  {category.title}
                </h3>
                <div class="space-y-3">
                  <For each={category.shortcuts}>
                    {(shortcut) => <ShortcutRow desc={shortcut.desc} keys={shortcut.keys} />}
                  </For>
                </div>
              </CardContent>
            </Card>
          )}
        </For>
      </div>
    </PageContainer>
  );
}

export const profileShortcutsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/profile/shortcuts",
  component: ShortcutsPage,
});
